import React from 'react';

const fmtCurrency = (n) => '$' + new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
const fmtPct = (x) => (Number.isFinite(x) ? (x * 100).toFixed(x * 100 < 10 ? 1 : 0) + '%' : '0%');

class ProfitGapResults extends React.Component {
  render() {
    const { vertical, totals, benchmarks, profit_gaps, onBack } = this.props;
    const { total_revenue, recasted_ebitda, ebitda_margin_current } = totals;
    const { low_margin, high_margin, avg_margin, benchmark_ebitda_low, benchmark_ebitda_high } = benchmarks;
    const { profit_gap_low, profit_gap_high } = profit_gaps;

    const gapLabel = (v) => v >= 0 ? 'shortfall' : 'surplus';
    // Marker helpers for plotting on bell curve
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const mapToXRaw = (m) => {
      const t = (m - low_margin) / Math.max(1e-6, (high_margin - low_margin));
      return 10 + clamp(t, 0, 1) * 80; // map low..high -> 10..90
    };
    const xAvgRaw = mapToXRaw(avg_margin || 0.228);
    const xUser = clamp(50 + (mapToXRaw(ebitda_margin_current || 0) - xAvgRaw), 0, 100);
    // Keep High at the rightmost band center
    const xHigh = 95;
    // Approximate bell curve Y position for a given x (0..100)
    const bellY = (x) => {
      const base = 38;      // baseline y used in path
      const amplitude = 26; // peak height relative to baseline
      const sigma = 20;     // width parameter
      return base - amplitude * Math.exp(-Math.pow(x - 50, 2) / (2 * Math.pow(sigma, 2)));
    };

    return (
      <div className="flex justify-center bg-succession-surface px-4 pt-2 pb-6">
        <div className="w-full max-w-3xl">
          <div className="mb-4">
            <button type="button" onClick={onBack} className="text-succession-primary hover:underline" aria-label="Go back">← Back</button>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-succession-foreground mb-2">Your Profit Gap</h1>
          <p className="text-succession-muted mb-4">Benchmarks use configurable EBITDA margin range saved to your company settings.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="p-4 rounded border border-succession-border bg-white">
              <div className="text-succession-muted text-sm">Total Revenue</div>
              <div className="text-2xl font-semibold text-succession-foreground">{fmtCurrency(total_revenue)}</div>
            </div>
            <div className="p-4 rounded border border-succession-border bg-white">
              <div className="text-succession-muted text-sm">Recasted EBITDA (Current)</div>
              <div className="text-2xl font-semibold text-succession-foreground">{fmtCurrency(recasted_ebitda)} <span className="ml-2 text-base text-succession-muted">({fmtPct(ebitda_margin_current)})</span></div>
            </div>
          </div>

          <div className="p-6 rounded-lg border border-succession-border bg-white mb-4 ring-1 ring-succession-primary/20">
            <div className="text-succession-muted text-sm mb-1">Profit Gap vs High Benchmark</div>
            <div className={`text-3xl md:text-4xl font-bold ${profit_gap_high >= 0 ? 'text-succession-error' : 'text-succession-success'}`}>{fmtCurrency(profit_gap_high)} <span className="ml-2 text-sm md:text-base text-succession-muted">({gapLabel(profit_gap_high)})</span></div>
            <div className="text-xs text-succession-muted mt-1">High benchmark set at {fmtPct(high_margin)} of revenue</div>
          </div>

          {/* Bell curve visualization */}
          <div className="p-4 rounded border border-succession-border bg-white mb-6">
            <div className="text-succession-muted text-sm mb-2">EBITDA Margin Distribution</div>
            <div className="relative w-full h-44">
              <svg viewBox="0 0 100 44" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                <defs>
                  {/* area under bell curve */}
                  <path id="bellArea" d="M0,38 C15,38 25,12 50,12 C75,12 85,38 100,38 L100,44 L0,44 Z" />
                  {/* segment clips */}
                  <clipPath id="segLow"><rect x="0" y="0" width="10" height="44" /></clipPath>
                  <clipPath id="segMid"><rect x="10" y="0" width="80" height="44" /></clipPath>
                  <clipPath id="segHigh"><rect x="90" y="0" width="10" height="44" /></clipPath>
                </defs>
                {/* filled segments under the curve */}
                <use href="#bellArea" clipPath="url(#segLow)" fill="var(--succession-error, #ef4444)" opacity="0.25" />
                <use href="#bellArea" clipPath="url(#segMid)" fill="var(--succession-primary, #5489B6)" opacity="0.18" />
                <use href="#bellArea" clipPath="url(#segHigh)" fill="var(--succession-success, #10b981)" opacity="0.25" />

                {/* outline on top */}
                <path d="M0,38 C15,38 25,12 50,12 C75,12 85,38 100,38" fill="none" stroke="var(--succession-border,#94a3b8)" strokeWidth="1.5" />

                {/* boundaries spanning chart height to emphasize segments */}
                <line x1="10" y1="42" x2="10" y2="2" stroke="var(--succession-border,#94a3b8)" strokeWidth="0.6" opacity="0.35" />
                <line x1="90" y1="42" x2="90" y2="2" stroke="var(--succession-border,#94a3b8)" strokeWidth="0.6" opacity="0.35" />

                {/* Plot markers */}
                <circle cx={xUser} cy={bellY(xUser)} r="1.8" fill="var(--succession-primary,#5489B6)" stroke="#ffffff" strokeWidth="0.6" />
                <circle cx={xHigh} cy={bellY(xHigh)} r="1.8" fill="var(--succession-success,#10b981)" stroke="#ffffff" strokeWidth="0.6" />
                {/* Note: labels will be overlaid as HTML to avoid SVG stretch issues */}
              </svg>
              {/* HTML labels positioned relative to markers */}
              <div className="absolute" style={{ left: xUser + '%', top: '6px', transform: 'translateX(-50%)' }}>
                <div className="text-[10px] md:text-xs font-medium text-succession-foreground text-center">You</div>
                <div className="text-[10px] md:text-xs text-succession-muted text-center">{fmtPct(ebitda_margin_current)}</div>
              </div>
              <div className="absolute" style={{ left: xHigh + '%', top: '6px', transform: 'translateX(-50%)' }}>
                <div className="text-[10px] md:text-xs font-medium text-succession-foreground text-center">High</div>
                <div className="text-[10px] md:text-xs text-succession-muted text-center">{fmtPct(high_margin)}</div>
              </div>
            </div>
            {/* Labels beneath the chart */}
            <div className="mt-2 grid grid-cols-3 text-xs">
              <div className="text-left">
                <div className="font-medium text-succession-foreground">Low Performers</div>
                <div className="text-succession-muted">{fmtPct(low_margin)}</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-succession-foreground">Average Performers</div>
                <div className="text-succession-muted">{fmtPct(avg_margin)}</div>
              </div>
              <div className="text-right">
                <div className="font-medium text-succession-foreground">Top Performers</div>
                <div className="text-succession-muted">{fmtPct(high_margin)}</div>
              </div>
            </div>
          </div>


          <div className="mt-6 p-4 rounded border border-succession-border bg-white">
            <div className="font-semibold text-succession-foreground mb-1">How it’s calculated</div>
            <ul className="list-disc ml-6 text-succession-muted text-sm space-y-1">
              <li>Revenue ({vertical === 'p_and_c' ? 'p_and_c' : 'wealth_rj'}) = {vertical === 'p_and_c' ? 'recurring + non‑recurring' : 'fee‑based + trailer + direct + transactional'}</li>
              <li>Recasted EBITDA (preferred) = Normalized EBITDA; (fallback) = total revenue − normalized operating expenses</li>
              <li>EBITDA margin (current) = recasted EBITDA ÷ total revenue</li>
              <li>Benchmark EBITDA (low/high) = total revenue × ({fmtPct(low_margin)} / {fmtPct(high_margin)})</li>
              <li>Profit Gap (low/high) = benchmark EBITDA (low/high) − recasted EBITDA</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

export default ProfitGapResults;


